// App Tracking Transparency (iOS) via cordova-plugin-idfa. Reading the status and
// asking for it are separate on purpose: the consent gates read it on every event,
// while the prompt belongs to an explicit user action (onboarding message, cookie settings).

import { sendAttTelemetry } from '../consent/consentTelemetry';

const getIdfaPlugin = () => window?.cordova?.plugins?.idfa;

// The plugin's numeric status as stored in vansite_consent_events.att_status; null where iOS
// reports none (iOS < 14).
const attStatusName = (idfaPlugin, permission) =>
  ({
    [idfaPlugin.TRACKING_PERMISSION_AUTHORIZED]: 'authorized',
    [idfaPlugin.TRACKING_PERMISSION_DENIED]: 'denied',
    [idfaPlugin.TRACKING_PERMISSION_RESTRICTED]: 'restricted',
    [idfaPlugin.TRACKING_PERMISSION_NOT_DETERMINED]: 'not_determined',
  })[permission] ?? null;

/**
 * Reads the ATT status without prompting.
 *
 * @returns {Promise<boolean>} true only when the user authorized tracking
 */
export const hasTrackingPermission = async () => {
  const idfaPlugin = getIdfaPlugin();
  if (!idfaPlugin) return false;
  const info = await idfaPlugin.getInfo();
  return info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_AUTHORIZED;
};

/**
 * Shows the ATT prompt while the status is undetermined. iOS shows it only once,
 * afterwards this just reports the decision. Every call sends the status to the consent
 * telemetry, an earlier decision included: reporting needs the ATT status of everyone who
 * granted marketing, not only of those prompted now.
 *
 * @returns {Promise<boolean>} true when tracking is authorized
 */
export const requestTrackingPermission = async () => {
  const idfaPlugin = getIdfaPlugin();
  if (!idfaPlugin) return false;
  const info = await idfaPlugin.getInfo();
  const permission =
    info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_NOT_DETERMINED
      ? await idfaPlugin.requestPermission()
      : info.trackingPermission;
  const attStatus = attStatusName(idfaPlugin, permission);
  if (attStatus) sendAttTelemetry(attStatus);
  return permission === idfaPlugin.TRACKING_PERMISSION_AUTHORIZED;
};

/**
 * Reads the ATT status without prompting, as the OneSignal `att_status` tag value. A restricted
 * status (parental controls, device management) can never show the dialog and counts as denied.
 *
 * @returns {Promise<'authorized'|'denied'|'not_determined'|null>} null without ATT (no plugin,
 *   Android, iOS < 14)
 */
export const getTrackingStatus = async () => {
  const idfaPlugin = getIdfaPlugin();
  if (!idfaPlugin) return null;
  const { trackingPermission } = await idfaPlugin.getInfo();
  const attStatus = attStatusName(idfaPlugin, trackingPermission);
  return attStatus === 'restricted' ? 'denied' : attStatus;
};
